/**
 * Owner: Masa Bayu
 * File: parts.ts
 * Date:Time: 2026-09-18 15:50
 * Description: IR block, section, asset, table, link, and warning parts.
 */

import {
  isQXDocumentBlockType,
  QX_DOCUMENT_ASSET_KINDS,
  QX_DOCUMENT_DIRECTIONS,
  QX_DOCUMENT_METADATA_SOURCES,
  QX_DOCUMENT_WARNING_SEVERITIES,
  type QXDocumentAssetKind,
  type QXDocumentBlockType,
  type QXDocumentDirection,
  type QXDocumentMetadataSource,
  type QXDocumentWarningSeverity,
} from './codes';
import { createQXDocumentConfidence, type QXDocumentConfidence } from './confidence';
import { assertConfidence, assertOptionalTrimmed, assertValidId, isRecord } from './guards';
import { createQXDocumentId } from './id';
import { createQXDocumentRaster, type QXDocumentRaster } from './raster';
import {
  createQXDocumentProvenance,
  type CreateQXDocumentProvenanceOptions,
  type QXDocumentProvenance,
} from './provenance';

export interface QXDocumentMetadataField {
  readonly value: string;
  readonly source: QXDocumentMetadataSource;
  readonly confidence: number;
  readonly verified: boolean;
}

export interface QXDocumentMetadata {
  readonly title: QXDocumentMetadataField | null;
  readonly authors: readonly QXDocumentMetadataField[];
  readonly language: QXDocumentMetadataField | null;
}

export interface QXDocumentLanguage {
  readonly document: string | null;
  readonly direction: QXDocumentDirection | null;
}

export interface QXDocumentBlock {
  readonly blockId: string;
  readonly type: QXDocumentBlockType;
  readonly parentId: string | null;
  readonly order: number;
  readonly text: string | null;
  readonly assetId: string | null;
  readonly language: string | null;
  readonly provenance: QXDocumentProvenance;
  readonly confidence: QXDocumentConfidence | null;
  readonly raster?: QXDocumentRaster;
}

export interface QXDocumentSection {
  readonly sectionId: string;
  readonly title: string | null;
  readonly headingBlockId: string | null;
  readonly childBlockIds: readonly string[];
  readonly language: string | null;
}

export interface QXDocumentAssetRef {
  readonly assetId: string;
  readonly contentHash: string | null;
  readonly kind: QXDocumentAssetKind;
  readonly provenance: QXDocumentProvenance;
}

export interface QXDocumentTable {
  readonly tableId: string;
  readonly rowCount: number;
  readonly columnCount: number;
  readonly blockId: string | null;
  readonly provenance: QXDocumentProvenance;
}

export interface QXDocumentLink {
  readonly linkId: string;
  readonly href: string;
  readonly blockId: string | null;
  readonly provenance: QXDocumentProvenance;
}

export interface QXDocumentWarning {
  readonly warningId: string;
  readonly severity: QXDocumentWarningSeverity;
  readonly code: string;
  readonly path: string;
}

export interface QXDocumentPageMapEntry {
  readonly page: number;
  readonly blockIds: readonly string[];
}

export interface CreateQXDocumentBlockOptions {
  readonly blockId?: string;
  readonly type: QXDocumentBlockType;
  readonly parentId?: string | null;
  readonly order: number;
  readonly text?: string | null;
  readonly assetId?: string | null;
  readonly language?: string | null;
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
  readonly confidence?: QXDocumentConfidence | null;
  readonly raster?: QXDocumentRaster | null;
}

export interface CreateQXDocumentMetadataFieldOptions {
  readonly value: string;
  readonly source: QXDocumentMetadataSource;
  readonly confidence: number;
  readonly verified?: boolean;
}

function assertEnum<T extends string>(value: unknown, allowed: readonly T[], label: string): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    throw new Error(`${label} is unknown`);
  }
  return value as T;
}

function assertNonNegativeInt(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative integer`);
  }
  return value;
}

export function createQXDocumentMetadataField(
  options: CreateQXDocumentMetadataFieldOptions,
): QXDocumentMetadataField {
  if (!isRecord(options)) {
    throw new Error('Metadata field must be an object');
  }
  const value = assertOptionalTrimmed(options.value, 'metadata.value');
  if (value === null) {
    throw new Error('metadata.value must be a non-empty string');
  }
  assertConfidence(options.confidence, 'metadata.confidence');
  return Object.freeze({
    value,
    source: assertEnum(options.source, QX_DOCUMENT_METADATA_SOURCES, 'metadata.source'),
    confidence: options.confidence,
    verified: options.verified === true,
  });
}

export function createQXDocumentBlock(options: CreateQXDocumentBlockOptions): QXDocumentBlock {
  if (!isRecord(options)) {
    throw new Error('Document block must be an object');
  }
  if (!isQXDocumentBlockType(options.type)) {
    throw new Error('Document block type is unknown');
  }
  const blockId = options.blockId ?? createQXDocumentId('irblk');
  assertValidId(blockId, 'block.blockId');
  const confidence =
    options.confidence === undefined || options.confidence === null
      ? null
      : createQXDocumentConfidence(options.confidence);
  return Object.freeze({
    blockId,
    type: options.type,
    parentId: assertOptionalTrimmed(options.parentId, 'block.parentId'),
    order: assertNonNegativeInt(options.order, 'block.order'),
    text: options.text === undefined ? null : assertOptionalTrimmed(options.text, 'block.text'),
    assetId: assertOptionalTrimmed(options.assetId, 'block.assetId'),
    language: assertOptionalTrimmed(options.language, 'block.language'),
    provenance: createQXDocumentProvenance(options.provenance),
    confidence,
    ...(options.raster ? { raster: createQXDocumentRaster(options.raster) } : {}),
  });
}

export function createQXDocumentSection(options: {
  readonly sectionId?: string;
  readonly title?: string | null;
  readonly headingBlockId?: string | null;
  readonly childBlockIds?: readonly string[];
  readonly language?: string | null;
}): QXDocumentSection {
  const sectionId = options.sectionId ?? createQXDocumentId('irsec');
  assertValidId(sectionId, 'section.sectionId');
  const childBlockIds = (options.childBlockIds ?? []).map((id) => {
    assertValidId(id, 'section.childBlockIds');
    return id;
  });
  return Object.freeze({
    sectionId,
    title: assertOptionalTrimmed(options.title, 'section.title'),
    headingBlockId: assertOptionalTrimmed(options.headingBlockId, 'section.headingBlockId'),
    childBlockIds: Object.freeze(childBlockIds),
    language: assertOptionalTrimmed(options.language, 'section.language'),
  });
}

export function createQXDocumentAssetRef(options: {
  readonly assetId?: string;
  readonly contentHash?: string | null;
  readonly kind: QXDocumentAssetKind;
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
}): QXDocumentAssetRef {
  const assetId = options.assetId ?? createQXDocumentId('irasset');
  assertValidId(assetId, 'asset.assetId');
  return Object.freeze({
    assetId,
    contentHash: assertOptionalTrimmed(options.contentHash, 'asset.contentHash'),
    kind: assertEnum(options.kind, QX_DOCUMENT_ASSET_KINDS, 'asset.kind'),
    provenance: createQXDocumentProvenance(options.provenance),
  });
}

export function createQXDocumentTable(options: {
  readonly tableId?: string;
  readonly rowCount: number;
  readonly columnCount: number;
  readonly blockId?: string | null;
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
}): QXDocumentTable {
  const tableId = options.tableId ?? createQXDocumentId('irtbl');
  assertValidId(tableId, 'table.tableId');
  return Object.freeze({
    tableId,
    rowCount: assertNonNegativeInt(options.rowCount, 'table.rowCount'),
    columnCount: assertNonNegativeInt(options.columnCount, 'table.columnCount'),
    blockId: assertOptionalTrimmed(options.blockId, 'table.blockId'),
    provenance: createQXDocumentProvenance(options.provenance),
  });
}

export function createQXDocumentLink(options: {
  readonly linkId?: string;
  readonly href: string;
  readonly blockId?: string | null;
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
}): QXDocumentLink {
  const linkId = options.linkId ?? createQXDocumentId('irlink');
  assertValidId(linkId, 'link.linkId');
  const href = assertOptionalTrimmed(options.href, 'link.href');
  if (href === null) {
    throw new Error('link.href must be a non-empty string');
  }
  return Object.freeze({
    linkId,
    href,
    blockId: assertOptionalTrimmed(options.blockId, 'link.blockId'),
    provenance: createQXDocumentProvenance(options.provenance),
  });
}

export function createQXDocumentWarning(options: {
  readonly warningId?: string;
  readonly severity: QXDocumentWarningSeverity;
  readonly code: string;
  readonly path: string;
}): QXDocumentWarning {
  const warningId = options.warningId ?? createQXDocumentId('irwarn');
  assertValidId(warningId, 'warning.warningId');
  const code = assertOptionalTrimmed(options.code, 'warning.code');
  const path = assertOptionalTrimmed(options.path, 'warning.path');
  if (code === null || path === null) {
    throw new Error('warning code and path are required');
  }
  return Object.freeze({
    warningId,
    severity: assertEnum(options.severity, QX_DOCUMENT_WARNING_SEVERITIES, 'warning.severity'),
    code,
    path,
  });
}

export function createQXDocumentPageMapEntry(options: {
  readonly page: number;
  readonly blockIds?: readonly string[];
}): QXDocumentPageMapEntry {
  if (typeof options.page !== 'number' || !Number.isInteger(options.page) || options.page < 1) {
    throw new Error('pageMap.page must be a positive integer');
  }
  const blockIds = (options.blockIds ?? []).map((id) => {
    assertValidId(id, 'pageMap.blockIds');
    return id;
  });
  return Object.freeze({
    page: options.page,
    blockIds: Object.freeze(blockIds),
  });
}

export function createQXDocumentLanguage(options?: {
  readonly document?: string | null;
  readonly direction?: QXDocumentDirection | null;
}): QXDocumentLanguage {
  const direction = options?.direction;
  if (direction !== undefined && direction !== null) {
    assertEnum(direction, QX_DOCUMENT_DIRECTIONS, 'language.direction');
  }
  return Object.freeze({
    document: assertOptionalTrimmed(options?.document, 'language.document'),
    direction: direction ?? null,
  });
}

export function createQXDocumentMetadata(options?: {
  readonly title?: CreateQXDocumentMetadataFieldOptions | QXDocumentMetadataField | null;
  readonly authors?: readonly (CreateQXDocumentMetadataFieldOptions | QXDocumentMetadataField)[];
  readonly language?: CreateQXDocumentMetadataFieldOptions | QXDocumentMetadataField | null;
}): QXDocumentMetadata {
  return Object.freeze({
    title:
      options?.title === undefined || options.title === null
        ? null
        : createQXDocumentMetadataField(options.title),
    authors: Object.freeze(
      (options?.authors ?? []).map((author) => createQXDocumentMetadataField(author)),
    ),
    language:
      options?.language === undefined || options.language === null
        ? null
        : createQXDocumentMetadataField(options.language),
  });
}
